import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchSnapshot, type DashboardSnapshot } from "./lib/api";
import { RefreshControl } from "./components/RefreshControl";
import { Tabs } from "./components/Tabs";
import { MarketPricesTab } from "./components/MarketPricesTab";
import { PowerOverviewTab } from "./components/PowerOverviewTab";
import { GasOverviewTab } from "./components/GasOverviewTab";
import { UploadedPfcTab } from "./components/UploadedPfcTab";
// Monte-Carlo QPFC tab intentionally hidden — kept in components/PfcTab.tsx and
// wired to /api/dashboard/qpfc for future reuse but not surfaced today. The
// "PFCs" tab below serves the uploaded-PFC feature (#123).
// import { PfcTab } from "./components/PfcTab";
import { ErrorBoundary } from "./components/ErrorBoundary";

type TabId = "prices" | "power" | "gas" | "pfcs";

const TAB_DEFS = [
  { id: "prices" as const, label: "Marktpreise" },
  { id: "power" as const, label: "Überblick Strom" },
  { id: "gas" as const, label: "Überblick Erdgas" },
  { id: "pfcs" as const, label: "PFCs" },
];

export default function App() {
  const [tab, setTab] = useState<TabId>("prices");
  const queryClient = useQueryClient();
  const snapshot = useQuery<DashboardSnapshot, Error>({
    queryKey: ["snapshot"],
    queryFn: () => fetchSnapshot(false),
  });

  const handleRefresh = async () => {
    // Force-skip the worker caches across all data sources. Each query
    // dispatches its own URL with refresh=true, so we kick off N forced
    // refetches and update React-Query state once each lands.
    const tasks: Promise<unknown>[] = [];
    tasks.push(
      fetchSnapshot(true).then((d) => queryClient.setQueryData(["snapshot"], d)),
    );
    if (tab === "power" || tab === "gas") {
      // Re-fetch the open tab's data set; let React-Query handle it via the
      // refetch hook on the cached queries (force-flag propagates via the URL
      // through each tab's component on next mount, but we can shortcut by
      // simply invalidating).
      tasks.push(
        queryClient.invalidateQueries({ queryKey: ["futures-history"] }),
        queryClient.invalidateQueries({
          queryKey: [tab === "power" ? "power-spot-series" : "gas-spot-series"],
        }),
      );
      if (tab === "power") {
        tasks.push(queryClient.invalidateQueries({ queryKey: ["power-generation"] }));
      }
      if (tab === "gas") {
        tasks.push(queryClient.invalidateQueries({ queryKey: ["gas-storage"] }));
      }
    }
    await Promise.all(tasks);
  };

  return (
    <div className="min-h-full flex flex-col">
      <header className="sticky top-0 z-10 bg-white/80 dark:bg-slate-950/80 backdrop-blur border-b border-slate-200 dark:border-slate-800">
        <div className="mx-auto max-w-screen-2xl px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-base sm:text-lg font-semibold tracking-tight truncate">
              en-concept · Live Marktpreise
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
              Quelle: Montel · Auto-Refresh alle 5 Min.
            </p>
          </div>
          <RefreshControl
            updatedAt={snapshot.data?.updatedAt ?? null}
            cached={snapshot.data?.cached ?? false}
            isFetching={snapshot.isFetching}
            onRefresh={handleRefresh}
          />
        </div>
        <div className="mx-auto max-w-screen-2xl px-4 sm:px-6 pb-2">
          <Tabs tabs={TAB_DEFS} active={tab} onChange={setTab} />
        </div>
      </header>

      <main className="mx-auto max-w-screen-2xl w-full px-4 sm:px-6 py-4 sm:py-6 grow">
        {snapshot.isError && tab === "prices" && (
          <ErrorBanner message={snapshot.error.message} onRetry={() => snapshot.refetch()} />
        )}
        {/* Each tab is wrapped in its own ErrorBoundary so a render-time
            crash in one chart (e.g. unexpected payload shape) only blanks
            that tab — the rest of the app keeps working and the user can
            switch tabs or hit "Erneut versuchen" to remount. */}
        {tab === "prices" && (
          <ErrorBoundary label="Marktpreise">
            <MarketPricesTab data={snapshot.data} isLoading={snapshot.isLoading} />
          </ErrorBoundary>
        )}
        {tab === "power" && (
          <ErrorBoundary label="Überblick Strom">
            <PowerOverviewTab />
          </ErrorBoundary>
        )}
        {tab === "gas" && (
          <ErrorBoundary label="Überblick Erdgas">
            <GasOverviewTab />
          </ErrorBoundary>
        )}
        {tab === "pfcs" && (
          <ErrorBoundary label="PFCs">
            <UploadedPfcTab />
          </ErrorBoundary>
        )}
      </main>

      <footer className="mx-auto max-w-screen-2xl w-full px-4 sm:px-6 py-4 text-xs text-slate-500 dark:text-slate-400">
        TTF, ICE und NYMEX-Preise aktuell nicht verfügbar (nicht über Montel abrufbar).
      </footer>
    </div>
  );
}

function ErrorBanner({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="mb-4 rounded-lg border border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/40 px-4 py-3 text-sm text-rose-900 dark:text-rose-200 flex items-center justify-between gap-3">
      <span>Fehler beim Laden: {message}</span>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-md bg-rose-900 text-white px-2.5 py-1 text-xs font-medium hover:bg-rose-800"
      >
        Erneut versuchen
      </button>
    </div>
  );
}
